import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { Hero } from './components/Hero';
const StatsAndTestimonials = lazy(() => import('./components/StatsAndTestimonials').then(m => ({
  default: m.StatsAndTestimonials
})));
const PainPoints = lazy(() => import('./components/PainPoints').then(m => ({
  default: m.PainPoints
})));
const Modules = lazy(() => import('./components/Modules').then(m => ({
  default: m.Modules
})));
const ContentPreview = lazy(() => import('./components/ContentPreview').then(m => ({
  default: m.ContentPreview
})));
const ToolsIncluded = lazy(() => import('./components/ToolsIncluded').then(m => ({
  default: m.ToolsIncluded
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const IsForYou = lazy(() => import('./components/IsForYou').then(m => ({
  default: m.IsForYou
})));
const Pricing = lazy(() => import('./components/Pricing').then(m => ({
  default: m.Pricing
})));
const CostComparison = lazy(() => import('./components/CostComparison').then(m => ({
  default: m.CostComparison
})));
const FinalCTA = lazy(() => import('./components/FinalCTA').then(m => ({
  default: m.FinalCTA
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const PurchaseToast = lazy(() => import('./components/PurchaseToast').then(m => ({
  default: m.PurchaseToast
})));
const HERO_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/7BkzCbELLsUwyvco8cmoUC/Texto_del_parrafo.webp';
export function App() {
  useEffect(() => {
    document.documentElement.lang = 'es-AR';
    document.title = 'El Maestro de las Mamparas™ — Todo tu taller de mamparas listo. Desde hoy.';
    const metaDesc = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    metaDesc.setAttribute('content', 'El Maestro de las Mamparas: El sistema N°1 para carpinteros e instaladores de mamparas de vidrio en Argentina. Medidas exactas, cristales sin error, instalación prolija y presupuestos con ganancia real.');
    if (!document.querySelector('meta[name="description"]')) document.head.appendChild(metaDesc);
    if (!document.querySelector('meta[name="viewport"]')) {
      const viewport = document.createElement('meta');
      viewport.setAttribute('name', 'viewport');
      viewport.setAttribute('content', 'width=device-width, initial-scale=1');
      document.head.appendChild(viewport);
    }
    let jsonLd: HTMLScriptElement | null = null;
    let canonicalEl: HTMLLinkElement | null = null;
    let robotsEl: HTMLMetaElement | null = null;
    let pixelLoaded = false;
    const interactionEvents: Array<keyof DocumentEventMap> = ['pointerdown', 'keydown', 'touchstart'];
    const onFirstInteraction = () => {
      loadPixel();
      interactionEvents.forEach(ev => document.removeEventListener(ev, onFirstInteraction as any));
    };
    const handleCheckoutClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement)?.closest('a') as HTMLAnchorElement | null;
      if (target && target.href && target.href.includes('fegion.shop/cart')) {
        loadPixel();
        if ((window as any).fbq) {
          ;
          (window as any).fbq('track', 'InitiateCheckout');
        }
      }
    };
    const PIXEL_ID = '904978025482552';
    const loadPixel = () => {
      if (pixelLoaded || (window as any).fbq) return;
      pixelLoaded = true;
      const pixelScript = document.createElement('script');
      pixelScript.async = true;
      pixelScript.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${PIXEL_ID}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(pixelScript);
      const pixelNoscript = document.createElement('noscript');
      const pixelImg = document.createElement('img');
      pixelImg.height = 1;
      pixelImg.width = 1;
      pixelImg.style.display = 'none';
      pixelImg.alt = '';
      pixelImg.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
      pixelNoscript.appendChild(pixelImg);
      document.head.appendChild(pixelNoscript);
    };
    const runDeferredSetup = () => {
      const ogTags = [{
        property: 'og:title',
        content: 'El Maestro de las Mamparas™ — Todo tu taller de mamparas listo. Desde hoy.'
      }, {
        property: 'og:description',
        content: 'El sistema N°1 para carpinteros e instaladores de mamparas de vidrio en Argentina. 8 Módulos + 3 Bonos.'
      }, {
        property: 'og:type',
        content: 'product'
      }, {
        property: 'og:image',
        content: HERO_IMAGE_URL
      }, {
        property: 'og:locale',
        content: 'es_AR'
      }];
      ogTags.forEach(({
        property,
        content
      }) => {
        const existing = document.querySelector(`meta[property="${property}"]`);
        if (!existing) {
          const meta = document.createElement('meta');
          meta.setAttribute('property', property);
          meta.setAttribute('content', content);
          document.head.appendChild(meta);
        }
      });
      jsonLd = document.createElement('script');
      jsonLd.type = 'application/ld+json';
      jsonLd.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'El Maestro de las Mamparas™',
        description: 'El sistema N°1 para carpinteros e instaladores de mamparas de vidrio en Argentina. 8 Módulos + 3 Bonos.',
        image: HERO_IMAGE_URL,
        brand: {
          '@type': 'Brand',
          name: 'El Maestro de las Mamparas'
        },
        offers: [{
          '@type': 'Offer',
          name: 'El Maestro de las Mamparas',
          price: '19990',
          priceCurrency: 'ARS',
          availability: 'https://schema.org/InStock',
          url: '#pricing'
        }, {
          '@type': 'Offer',
          name: 'Pack Maestro Pro',
          price: '29990',
          priceCurrency: 'ARS',
          availability: 'https://schema.org/InStock',
          url: '#pricing'
        }],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '212'
        }
      });
      document.head.appendChild(jsonLd);
      if (!document.querySelector('link[rel="canonical"]')) {
        canonicalEl = document.createElement('link');
        canonicalEl.rel = 'canonical';
        canonicalEl.href = window.location.origin + window.location.pathname;
        document.head.appendChild(canonicalEl);
      }
      if (!document.querySelector('meta[name="robots"]')) {
        robotsEl = document.createElement('meta');
        robotsEl.setAttribute('name', 'robots');
        robotsEl.setAttribute('content', 'index, follow');
        document.head.appendChild(robotsEl);
      }
      if (!document.querySelector('meta[name="theme-color"]')) {
        const themeColor = document.createElement('meta');
        themeColor.setAttribute('name', 'theme-color');
        themeColor.setAttribute('content', '#0a1628');
        document.head.appendChild(themeColor);
      }
      interactionEvents.forEach(ev => document.addEventListener(ev, onFirstInteraction as any, {
        once: true,
        passive: true
      } as any));
      document.addEventListener('click', handleCheckoutClick);
    };
    const prefetchChunks = () => {
      import('./components/StatsAndTestimonials');
      import('./components/PainPoints');
      import('./components/Modules');
      import('./components/Bonuses');
      import('./components/IsForYou');
      import('./components/Pricing');
      import('./components/CostComparison');
      import('./components/FinalCTA');
      import('./components/Footer');
      import('./components/PurchaseToast');
    };
    const ric: any = (window as any).requestIdleCallback || ((cb: any) => setTimeout(cb, 1));
    const cic: any = (window as any).cancelIdleCallback || ((id: any) => clearTimeout(id));
    const prefetchId = ric(prefetchChunks, {
      timeout: 500
    });
    const idleId = ric(runDeferredSetup, {
      timeout: 1500
    });
    return () => {
      cic(idleId);
      cic(prefetchId);
      if (jsonLd) jsonLd.remove();
      if (canonicalEl) canonicalEl.remove();
      if (robotsEl) robotsEl.remove();
      interactionEvents.forEach(ev => document.removeEventListener(ev, onFirstInteraction as any));
      document.removeEventListener('click', handleCheckoutClick);
    };
  }, []);
  return <div className="min-h-screen bg-background text-gray-300 font-body selection:bg-gold selection:text-background" data-id="element-0">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-gold focus:text-background focus:px-4 focus:py-2 focus:rounded-lg focus:font-bold" data-id="element-1">
        Saltar al contenido principal
      </a>
      <main id="main-content" data-id="element-2">
        <Hero data-id="element-3" />
        <Suspense fallback={null} data-id="element-4">
          <StatsAndTestimonials data-id="element-5" />
          <PainPoints data-id="element-6" />
          <Modules data-id="element-7" />
          <Bonuses data-id="element-8" />
          <IsForYou data-id="element-9" />
          <Pricing data-id="element-10" />
          <CostComparison data-id="element-11" />
          <FinalCTA data-id="element-12" />
        </Suspense>
      </main>
      <Suspense fallback={null} data-id="element-13">
        <Footer data-id="element-14" />
        <PurchaseToast data-id="element-15" />
      </Suspense>
    </div>;
}